import { throttle } from 'lodash-es';
import type { SafeResizeObserver } from './createSafeResizeObserver.types';

const THROTTLE_MS = 100;

type CreateSafeResizeObserverOptions = Readonly<{
  /**
   * Throttle notifications to one per 100ms. `false` skips throttling: every
   * notification is deferred to the next animation frame, coalesced per frame
   * to the latest entry per target.
   * Default: true, or false when `syncInitialDelivery` is set.
   */
  throttle?: boolean;
  /**
   * When true, the observer's first delivery after `observe()` runs
   * synchronously (no throttle, no rAF), so the initial measure lands before
   * the first paint and there is no visible layout jump. For a zero-size
   * target that delivery is the first non-zero size. Only the observer's very
   * first delivery is synchronous: calling `observe()` again after a delivery
   * does not re-arm it. Every later notification is deferred as usual.
   *
   * The synchronous delivery still runs inside the browser's ResizeObserver
   * loop: it must not change the size of an observed element, or the browser
   * reports "ResizeObserver loop completed with undelivered notifications".
   * Measure once in a layout effect before observing, so this delivery finds
   * nothing to change. Default: false.
   */
  syncInitialDelivery?: boolean;
}>;

/**
 * Creates a ResizeObserver that prevents "ResizeObserver loop" errors by
 * throttling callbacks and deferring them to requestAnimationFrame.
 *
 * Drop-in replacement for `new ResizeObserver(callback)`:
 *
 * @example
 * const observer = createSafeResizeObserver((entries) => {
 *   doSomething(entries[0]);
 * });
 * observer.observe(element);
 * // cleanup:
 * observer.disconnect();
 *
 * Uses lodash throttle with default options (leading: true, trailing: true),
 * so the callback fires on the first resize event without waiting for the
 * throttle interval, and at most once per 100ms thereafter. The trailing
 * call guarantees the last event is never dropped. Notifications that arrive
 * while a delivery is pending are merged, keeping the latest entry per target.
 *
 * The callback is deferred through requestAnimationFrame, so layout changes it
 * makes happen outside the browser's ResizeObserver loop. Callers that need the
 * initial measure before first paint pass `{ syncInitialDelivery: true }`.
 *
 * @param callback - The ResizeObserver callback to invoke safely
 * @param options  - Optional configuration (throttle, syncInitialDelivery)
 */
export function createSafeResizeObserver(callback: ResizeObserverCallback, options?: CreateSafeResizeObserverOptions): SafeResizeObserver {
  const { syncInitialDelivery = false, throttle: shouldThrottle = !syncInitialDelivery } = options ?? {};

  let frameId: number | null = null;
  let isSyncDeliveryPending = syncInitialDelivery;
  let isDeliveryPending = false;
  const pendingEntries = new Map<Element, ResizeObserverEntry>();

  const cancelFrame = () => {
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
  };

  const deferToFrame = (obs: ResizeObserver) => {
    cancelFrame();
    frameId = requestAnimationFrame(() => {
      frameId = null;
      // Covers the trailing throttle call after the leading frame already delivered.
      if (!isDeliveryPending) return;
      isDeliveryPending = false;
      const entries = Array.from(pendingEntries.values());
      pendingEntries.clear();
      callback(entries, obs);
    });
  };

  const throttledDeferToFrame = shouldThrottle ? throttle(deferToFrame, THROTTLE_MS) : null;

  const observer = new ResizeObserver((entries, obs) => {
    if (isSyncDeliveryPending) {
      isSyncDeliveryPending = false;
      callback(entries, obs);
      return;
    }

    // `entries` can be missing or empty only from test mocks; the notification is still delivered.
    for (const entry of entries) {
      pendingEntries.set(entry.target, entry);
    }
    isDeliveryPending = true;
    (throttledDeferToFrame ?? deferToFrame)(obs);
  });

  return {
    observe(target, observeOptions) {
      observer.observe(target, observeOptions);
    },
    unobserve(target) {
      if (pendingEntries.delete(target) && pendingEntries.size === 0) isDeliveryPending = false;
      observer.unobserve(target);
    },
    disconnect() {
      throttledDeferToFrame?.cancel();
      cancelFrame();
      isDeliveryPending = false;
      pendingEntries.clear();
      observer.disconnect();
    },
  };
}
